/**
 * System Service
 * 
 * Handles all system-related API calls.
 */

import api, { getApiUrl, getAuthToken } from '../utils/api';

/**
 * Get scheduler status
 * @returns {Promise} - Get scheduler status response
 */
export const getSchedulerStatus = async () => {
  try {
    const response = await api.get('/v1/system/scheduler/status');

    if (response.status === 'success' && response.data) {
      return {
        success: true,
        data: response.data,
        message: response.message || 'Scheduler status retrieved successfully',
      };
    }

    return {
      success: false,
      error: response.message || 'Failed to retrieve scheduler status',
      data: null,
    };
  } catch (error) {
    console.error('Get scheduler status error:', error);
    
    let errorMessage = 'Failed to retrieve scheduler status. Please try again.';
    
    if (error.response) {
      const errorData = error.response;
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      data: null,
    };
  }
};

/**
 * Trigger a scheduler job
 * @param {string} jobId - Job ID to trigger
 * @returns {Promise} - Trigger job response
 */
export const triggerJob = async (jobId) => {
  try {
    const response = await api.post(`/v1/system/scheduler/trigger/${jobId}`);

    if (response.status === 'success' && response.data) {
      return {
        success: true,
        data: response.data,
        message: response.message || 'Job triggered successfully',
      };
    }

    return {
      success: false,
      error: response.message || 'Failed to trigger job',
      data: null,
    };
  } catch (error) {
    console.error('Trigger job error:', error);
    
    let errorMessage = 'Failed to trigger job. Please try again.';
    
    if (error.response) {
      const errorData = error.response;
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      } else if (errorData.details && Array.isArray(errorData.details)) {
        const detailMessages = errorData.details.map(d => d.message).join(', ');
        if (detailMessages) {
          errorMessage = detailMessages;
        }
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      data: null,
    };
  }
};

/**
 * Clear database (destructive operation)
 * @returns {Promise} - Clear database response
 */
export const clearDatabase = async () => {
  try {
    const response = await api.delete('/v1/system/database/clear');

    if (response.status === 'success') {
      return {
        success: true,
        message: response.message || 'Database cleared successfully',
      };
    }

    return {
      success: false,
      error: response.message || 'Failed to clear database',
    };
  } catch (error) {
    console.error('Clear database error:', error);
    
    let errorMessage = 'Failed to clear database. Please try again.';
    
    if (error.response) {
      const errorData = error.response;
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
    };
  }
};

/**
 * Validate credentials (login)
 * @param {string} email - User email
 * @param {string} password - User password
 * @returns {Promise} - Login response
 */
export const validateCredentials = async (email, password) => {
  try {
    const response = await api.post('/v1/api/auth/login', {
      email,
      password,
    });

    if (response.status === 'success' && response.data) {
      return {
        success: true,
        message: response.message || 'Credentials validated successfully',
        data: response.data,
      };
    }

    return {
      success: false,
      error: response.message || 'Invalid credentials',
    };
  } catch (error) {
    console.error('Validate credentials error:', error);
    
    let errorMessage = 'Invalid credentials. Please try again.';
    
    if (error.response) {
      const errorData = error.response;
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
    };
  }
};

/**
 * Get tuition settings
 * @returns {Promise} - Get tuition settings response
 */
export const getTuitionSettings = async () => {
  try {
    const response = await api.get('/v1/system/tuition-settings');

    if (response.status === 'success' && response.data) {
      return {
        success: true,
        data: response.data,
        message: response.message || 'Tuition settings retrieved successfully',
      };
    }

    return {
      success: false,
      error: response.message || 'Failed to retrieve tuition settings',
      data: null,
    };
  } catch (error) {
    console.error('Get tuition settings error:', error);
    
    let errorMessage = 'Failed to retrieve tuition settings. Please try again.';
    
    if (error.response) {
      const errorData = error.response;
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      data: null,
    };
  }
};

/**
 * Update tuition settings
 * @param {Object} settings - Tuition settings object with phase_1, phase_2, phase_3
 * @returns {Promise} - Update tuition settings response
 */
export const updateTuitionSettings = async (settings) => {
  try {
    const response = await api.post('/v1/system/tuition-settings', settings);

    if (response.status === 'success' && response.data) {
      return {
        success: true,
        data: response.data,
        message: response.message || 'Tuition settings updated successfully',
      };
    }

    return {
      success: false,
      error: response.message || 'Failed to update tuition settings',
      data: null,
    };
  } catch (error) {
    console.error('Update tuition settings error:', error);
    
    let errorMessage = 'Failed to update tuition settings. Please try again.';
    
    if (error.response) {
      const errorData = error.response;
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      } else if (errorData.details && Array.isArray(errorData.details)) {
        const detailMessages = errorData.details.map(d => d.message).join(', ');
        if (detailMessages) {
          errorMessage = detailMessages;
        }
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      data: null,
    };
  }
};

/**
 * Get monitoring recipients
 * @param {Object} params - Query parameters (skip, limit)
 * @returns {Promise} - Get monitoring recipients response
 */
export const getMonitoringRecipients = async (params = {}) => {
  try {
    const queryParams = {};
    if (params.skip !== undefined) {
      queryParams.skip = params.skip;
    }
    if (params.limit !== undefined) {
      queryParams.limit = params.limit;
    }

    const queryString = new URLSearchParams(queryParams).toString();
    const endpoint = `/v1/system/monitoring/recipients${queryString ? `?${queryString}` : ''}`;
    
    const response = await api.get(endpoint);

    if (response.status === 'success' && response.data) {
      return {
        success: true,
        data: response.data,
        pagination: response.pagination,
        message: response.message || 'Monitoring recipients retrieved successfully',
      };
    }

    return {
      success: false,
      error: response.message || 'Failed to retrieve monitoring recipients',
      data: null,
      pagination: null,
    };
  } catch (error) {
    console.error('Get monitoring recipients error:', error);
    
    let errorMessage = 'Failed to retrieve monitoring recipients. Please try again.';
    
    if (error.response) {
      const errorData = error.response;
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      data: null,
      pagination: null,
    };
  }
};

/**
 * Get monitoring recipient by ID
 * @param {string} recipientId - Recipient ID
 * @returns {Promise} - Get monitoring recipient response
 */
export const getMonitoringRecipient = async (recipientId) => {
  try {
    const response = await api.get(`/v1/system/monitoring/recipients/${recipientId}`);

    if (response.status === 'success' && response.data) {
      return {
        success: true,
        data: response.data,
        message: response.message || 'Monitoring recipient retrieved successfully',
      };
    }

    return {
      success: false,
      error: response.message || 'Failed to retrieve monitoring recipient',
      data: null,
    };
  } catch (error) {
    console.error('Get monitoring recipient error:', error);
    
    let errorMessage = 'Failed to retrieve monitoring recipient. Please try again.';
    
    if (error.response) {
      const errorData = error.response;
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      data: null,
    };
  }
};

/**
 * Create monitoring recipient
 * @param {Object} recipientData - Recipient data (email, name, is_active)
 * @returns {Promise} - Create monitoring recipient response
 */
export const createMonitoringRecipient = async (recipientData) => {
  try {
    const response = await api.post('/v1/system/monitoring/recipients', recipientData);

    if (response.status === 'success' && response.data) {
      return {
        success: true,
        data: response.data,
        message: response.message || 'Monitoring recipient created successfully',
      };
    }

    return {
      success: false,
      error: response.message || 'Failed to create monitoring recipient',
      data: null,
    };
  } catch (error) {
    console.error('Create monitoring recipient error:', error);
    
    let errorMessage = 'Failed to create monitoring recipient. Please try again.';
    
    if (error.response) {
      const errorData = error.response;
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      } else if (errorData.details && Array.isArray(errorData.details)) {
        const detailMessages = errorData.details.map(d => d.message).join(', ');
        if (detailMessages) {
          errorMessage = detailMessages;
        }
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      data: null,
    };
  }
};

/**
 * Update monitoring recipient
 * @param {string} recipientId - Recipient ID
 * @param {Object} recipientData - Recipient data (email, name, is_active)
 * @returns {Promise} - Update monitoring recipient response
 */
export const updateMonitoringRecipient = async (recipientId, recipientData) => {
  try {
    const response = await api.put(`/v1/system/monitoring/recipients/${recipientId}`, recipientData);

    if (response.status === 'success' && response.data) {
      return {
        success: true,
        data: response.data,
        message: response.message || 'Monitoring recipient updated successfully',
      };
    }

    return {
      success: false,
      error: response.message || 'Failed to update monitoring recipient',
      data: null,
    };
  } catch (error) {
    console.error('Update monitoring recipient error:', error);
    
    let errorMessage = 'Failed to update monitoring recipient. Please try again.';
    
    if (error.response) {
      const errorData = error.response;
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      } else if (errorData.details && Array.isArray(errorData.details)) {
        const detailMessages = errorData.details.map(d => d.message).join(', ');
        if (detailMessages) {
          errorMessage = detailMessages;
        }
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      data: null,
    };
  }
};

/**
 * Delete monitoring recipient
 * @param {string} recipientId - Recipient ID
 * @returns {Promise} - Delete monitoring recipient response
 */
export const deleteMonitoringRecipient = async (recipientId) => {
  try {
    const response = await api.delete(`/v1/system/monitoring/recipients/${recipientId}`);

    if (response.status === 'success') {
      return {
        success: true,
        message: response.message || 'Monitoring recipient deleted successfully',
      };
    }

    return {
      success: false,
      error: response.message || 'Failed to delete monitoring recipient',
    };
  } catch (error) {
    console.error('Delete monitoring recipient error:', error);
    
    let errorMessage = 'Failed to delete monitoring recipient. Please try again.';
    
    if (error.response) {
      const errorData = error.response;
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
    };
  }
};

/**
 * Get daily monitoring reports
 * @param {Object} params - Query parameters (skip, limit)
 * @returns {Promise} - Get daily reports response
 */
export const getDailyReports = async (params = {}) => {
  try {
    const queryParams = {};
    if (params.skip !== undefined) {
      queryParams.skip = params.skip;
    }
    if (params.limit !== undefined) {
      queryParams.limit = params.limit;
    }

    const queryString = new URLSearchParams(queryParams).toString();
    const endpoint = `/v1/system/monitoring/reports/daily${queryString ? `?${queryString}` : ''}`;
    
    const response = await api.get(endpoint);

    if (response.status === 'success' && response.data) {
      return {
        success: true,
        data: response.data,
        pagination: response.pagination,
        message: response.message || 'Daily reports retrieved successfully',
      };
    }

    return {
      success: false,
      error: response.message || 'Failed to retrieve daily reports',
      data: null,
      pagination: null,
    };
  } catch (error) {
    console.error('Get daily reports error:', error);
    
    let errorMessage = 'Failed to retrieve daily reports. Please try again.';
    
    if (error.response) {
      const errorData = error.response;
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      data: null,
      pagination: null,
    };
  }
};

/**
 * Get daily report by date
 * @param {string} reportDate - Report date in YYYY-MM-DD format
 * @returns {Promise} - Get daily report response
 */
export const getDailyReportByDate = async (reportDate) => {
  try {
    const response = await api.get(`/v1/system/monitoring/reports/daily/${reportDate}`);

    if (response.status === 'success' && response.data) {
      return {
        success: true,
        data: response.data,
        message: response.message || 'Daily report retrieved successfully',
      };
    }

    return {
      success: false,
      error: response.message || 'Failed to retrieve daily report',
      data: null,
    };
  } catch (error) {
    console.error('Get daily report by date error:', error);
    
    let errorMessage = 'Failed to retrieve daily report. Please try again.';
    
    if (error.response) {
      const errorData = error.response;
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      data: null,
    };
  }
};

/**
 * Get report files
 * @returns {Promise} - Get report files response
 */
export const getReportFiles = async () => {
  try {
    const response = await api.get('/v1/system/monitoring/reports/files');

    if (response.status === 'success' && response.data) {
      return {
        success: true,
        data: response.data,
        message: response.message || 'Report files retrieved successfully',
      };
    }

    return {
      success: false,
      error: response.message || 'Failed to retrieve report files',
      data: null,
    };
  } catch (error) {
    console.error('Get report files error:', error);
    
    let errorMessage = 'Failed to retrieve report files. Please try again.';
    
    if (error.response) {
      const errorData = error.response;
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      data: null,
    };
  }
};

/**
 * Run scheduler report tasks (hourly stats + daily report)
 * POST /v1/system/monitoring/reports/run-scheduler
 * Runs hourly statistics and daily report (PDF + email to active recipients).
 * @returns {Promise} - { success, status: 'success'|'partial', data: { hourly, daily, errors } }
 */
export const runSchedulerReports = async () => {
  try {
    const response = await api.post('/v1/system/monitoring/reports/run-scheduler');

    if (response.status === 'success' || response.status === 'partial') {
      return {
        success: true,
        status: response.status,
        data: response.data || { hourly: 'unknown', daily: 'unknown', errors: [] },
        message: response.message || (response.status === 'success' ? 'Scheduler tasks completed.' : 'Scheduler completed with some failures.'),
      };
    }

    return {
      success: false,
      status: response.status,
      error: response.message || 'Failed to run scheduler reports',
      data: response.data || null,
    };
  } catch (error) {
    console.error('Run scheduler reports error:', error);

    let errorMessage = 'Failed to run scheduler reports. Please try again.';
    if (error.response) {
      const errorData = error.response;
      if (errorData.error) errorMessage = errorData.error;
      else if (errorData.message) errorMessage = errorData.message;
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      data: null,
    };
  }
};

/**
 * Generate monitor link
 * @param {number} expiresInMinutes - Link expiration in minutes (1-1440)
 * @returns {Promise} - Generate monitor link response
 */
export const generateMonitorLink = async (expiresInMinutes = 60) => {
  try {
    const queryParams = {};
    if (expiresInMinutes) {
      queryParams.expires_in_minutes = expiresInMinutes;
    }

    const queryString = new URLSearchParams(queryParams).toString();
    const endpoint = `/v1/system/monitor/generate-link${queryString ? `?${queryString}` : ''}`;
    
    const response = await api.post(endpoint);

    if (response.status === 'success' && response.data) {
      return {
        success: true,
        data: response.data,
        message: response.message || 'Monitor link generated successfully',
      };
    }

    return {
      success: false,
      error: response.message || 'Failed to generate monitor link',
      data: null,
    };
  } catch (error) {
    console.error('Generate monitor link error:', error);
    
    let errorMessage = 'Failed to generate monitor link. Please try again.';
    
    if (error.response) {
      const errorData = error.response;
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      data: null,
    };
  }
};

/**
 * Backup MongoDB database
 * POST /v1/system/backup
 * Downloads backup archive file.
 */
export const backupDatabase = async () => {
  try {
    const url = getApiUrl('/v1/system/backup');
    const token = getAuthToken();

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/octet-stream,application/gzip,application/x-gzip,application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || errorData.error || `HTTP error! status: ${response.status}`);
    }

    const contentDisposition = response.headers.get('content-disposition');
    let filename = `mongo-backup-${new Date().toISOString().replace(/[:.]/g, '-')}.gz`;
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '');
      }
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);

    return {
      success: true,
      message: 'Backup created successfully',
    };
  } catch (error) {
    console.error('Backup database error:', error);
    return {
      success: false,
      error: error?.message || 'Failed to backup database. Please try again.',
    };
  }
};

/**
 * Restore MongoDB database from backup file (.gz)
 * POST /v1/system/restore
 */
export const restoreDatabase = async (file) => {
  try {
    if (!file) {
      return {
        success: false,
        error: 'Backup file is required',
      };
    }

    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post('/v1/system/restore', formData);

    if (response?.status === 'success' || response?.message) {
      return {
        success: true,
        data: response?.data || null,
        message: response?.message || 'Database restored successfully',
      };
    }

    return {
      success: false,
      error: response?.message || 'Failed to restore database',
      data: null,
    };
  } catch (error) {
    console.error('Restore database error:', error);

    let errorMessage = 'Failed to restore database. Please try again.';
    if (error?.response?.detail && Array.isArray(error.response.detail)) {
      errorMessage = error.response.detail.map((d) => d.msg).join(', ');
    } else if (error?.response?.error) {
      errorMessage = error.response.error;
    } else if (error?.response?.message) {
      errorMessage = error.response.message;
    } else if (error?.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      data: null,
    };
  }
};

const systemService = {
  getSchedulerStatus,
  triggerJob,
  clearDatabase,
  validateCredentials,
  backupDatabase,
  restoreDatabase,
  getTuitionSettings,
  updateTuitionSettings,
  getMonitoringRecipients,
  getMonitoringRecipient,
  createMonitoringRecipient,
  updateMonitoringRecipient,
  deleteMonitoringRecipient,
  getDailyReports,
  getDailyReportByDate,
  getReportFiles,
  runSchedulerReports,
  generateMonitorLink,
};
export default systemService;

